import {
  DESKTOP_ACTIVE_USERS_PERCENT,
  DESKTOP_EXPECTED_SAVING,
  DESKTOP_INTRANET_CHURN_IMPACT,
  DESKTOP_PRODUCTIVITY_IMPROVEMENT,
  DESKTOP_REPLACEMENT_COST_PERCENT,
  DESKTOP_TIME_CAPTURED_PERCENT,
  DESKTOP_TIME_CAPTURED_PRODUCTIVITY,
  FRONTLINE_ACTIVE_USERS_PERCENT,
  FRONTLINE_EXPECTED_SAVING,
  FRONTLINE_INTRANET_CHURN_IMPACT,
  FRONTLINE_PRODUCTIVITY_IMPROVEMENT,
  FRONTLINE_REPLACEMENT_COST_PERCENT,
  FRONTLINE_TIME_CAPTURED_PERCENT,
  FRONTLINE_TIME_CAPTURED_PRODUCTIVITY,
  INFLATION_INCREASE,
  PRODUCTIVITY_LEVEL_FIRST_THREE_MONTHS,
  UNFORESEEABLE_BUSINESS_CHANGE,
  WORKING_HOURS_PER_MONTH,
  WORKING_HOURS_PER_WEEK,
} from './constants';
import {
  type CurrencyConfig,
  type OnboardingDetails,
  type ProductivityDetails,
  type RetentionDetails,
  type RoiResults,
  type SliderInput,
} from './types';

// ─── Helpers ────────────────────────────────────────────────────
function growEmployees(base: number, growthPercent: number): [number, number, number] {
  const y1 = Math.round(base);
  const y2 = Math.round(y1 + (y1 * growthPercent) / 100);
  const y3 = Math.round(y2 + (y2 * growthPercent) / 100);
  return [y1, y2, y3];
}

function inflateRate(base: number): [number, number, number] {
  const y1 = base;
  const y2 = Math.round((y1 * INFLATION_INCREASE) / 100 + y1);
  const y3 = Math.round((y2 * INFLATION_INCREASE) / 100 + y2);
  return [y1, y2, y3];
}

function inflateSalary(base: number): [number, number, number] {
  const y1 = Math.round(base);
  const y2 = Math.round(base + (base * 3) / 100);
  const y3 = Math.round(y2 + (y2 * 3) / 100);
  return [y1, y2, y3];
}

function adjustForRisk(value: number): number {
  return Math.round(value - (UNFORESEEABLE_BUSINESS_CHANGE / 100) * value);
}

// ─── Productivity Yield ─────────────────────────────────────────
export function calculateProductivity(
  inputs: SliderInput,
  currencyConfig: CurrencyConfig,
): ProductivityDetails {
  const totalDesktop = (inputs.totalEmployees * inputs.desktopWorkersPercent) / 100;
  const totalFrontline = Math.round(inputs.totalEmployees - totalDesktop);

  const desktopEmployees = growEmployees(totalDesktop, inputs.predictedGrowthPercent);
  const frontlineEmployees = growEmployees(totalFrontline, inputs.predictedGrowthPercent);

  const desktopHourlyBase = Math.round(
    inputs.averageDesktopSalary / currencyConfig.workingHoursPerYear,
  );
  const frontlineHourlyBase = Math.round(
    inputs.averageFrontlineSalary / currencyConfig.workingHoursPerYear,
  );

  const desktopHourlyRate = inflateRate(desktopHourlyBase);
  const frontlineHourlyRate = inflateRate(frontlineHourlyBase);

  // Desktop
  const desktopActiveUsers: [number, number, number] = [
    Math.round((desktopEmployees[0] * DESKTOP_ACTIVE_USERS_PERCENT[0]) / 100),
    Math.round((desktopEmployees[1] * DESKTOP_ACTIVE_USERS_PERCENT[1]) / 100),
    Math.round((desktopEmployees[2] * DESKTOP_ACTIVE_USERS_PERCENT[2]) / 100),
  ];

  const desktopHoursSaved: [number, number, number] = [0, 0, 0].map((_, i) => {
    const v1 = WORKING_HOURS_PER_WEEK * currencyConfig.workingWeeksPerYear * desktopActiveUsers[i];
    return Math.round((v1 / 100) * DESKTOP_EXPECTED_SAVING);
  }) as [number, number, number];

  const desktopHoursCaptured: [number, number, number] = desktopHoursSaved.map((h) =>
    Math.round((DESKTOP_TIME_CAPTURED_PERCENT / 100) * h),
  ) as [number, number, number];

  const desktopValueCaptured: [number, number, number] = [0, 0, 0].map((_, i) =>
    Math.round(desktopHoursCaptured[i] * desktopHourlyRate[i]),
  ) as [number, number, number];

  const desktopAdjusted: [number, number, number] = desktopValueCaptured.map(adjustForRisk) as [
    number,
    number,
    number,
  ];
  const desktopAdjustedTotal = Math.round(
    desktopAdjusted[0] + desktopAdjusted[1] + desktopAdjusted[2],
  );

  // Frontline
  const frontlineActiveUsers: [number, number, number] = [
    Math.round((frontlineEmployees[0] * FRONTLINE_ACTIVE_USERS_PERCENT[0]) / 100),
    Math.round((frontlineEmployees[1] * FRONTLINE_ACTIVE_USERS_PERCENT[1]) / 100),
    Math.round((frontlineEmployees[2] * FRONTLINE_ACTIVE_USERS_PERCENT[2]) / 100),
  ];

  const frontlineHoursSaved: [number, number, number] = [0, 0, 0].map((_, i) => {
    const v1 =
      WORKING_HOURS_PER_WEEK * currencyConfig.workingWeeksPerYear * frontlineActiveUsers[i];
    return Math.round((v1 / 100) * FRONTLINE_EXPECTED_SAVING);
  }) as [number, number, number];

  const frontlineHoursCaptured: [number, number, number] = frontlineHoursSaved.map((h) =>
    Math.round((FRONTLINE_TIME_CAPTURED_PERCENT / 100) * h),
  ) as [number, number, number];

  const frontlineValueCaptured: [number, number, number] = [0, 0, 0].map((_, i) =>
    Math.round(frontlineHoursCaptured[i] * frontlineHourlyRate[i]),
  ) as [number, number, number];

  const frontlineAdjusted: [number, number, number] = frontlineValueCaptured.map(adjustForRisk) as [
    number,
    number,
    number,
  ];
  const frontlineAdjustedTotal = Math.round(
    frontlineAdjusted[0] + frontlineAdjusted[1] + frontlineAdjusted[2],
  );

  return {
    desktop: {
      employees: desktopEmployees,
      activeUsers: desktopActiveUsers,
      activePercent: DESKTOP_ACTIVE_USERS_PERCENT,
      hourlyRate: desktopHourlyRate,
      expectedSaving: DESKTOP_EXPECTED_SAVING,
      hoursSaved: desktopHoursSaved,
      hoursCaptured: desktopHoursCaptured,
      valueOfTimeCaptured: desktopValueCaptured,
      adjustedValue: desktopAdjusted,
      adjustedTotal: desktopAdjustedTotal,
    },
    frontline: {
      employees: frontlineEmployees,
      activeUsers: frontlineActiveUsers,
      activePercent: FRONTLINE_ACTIVE_USERS_PERCENT,
      hourlyRate: frontlineHourlyRate,
      expectedSaving: FRONTLINE_EXPECTED_SAVING,
      hoursSaved: frontlineHoursSaved,
      hoursCaptured: frontlineHoursCaptured,
      valueOfTimeCaptured: frontlineValueCaptured,
      adjustedValue: frontlineAdjusted,
      adjustedTotal: frontlineAdjustedTotal,
    },
  };
}

// ─── Onboarding Gains ───────────────────────────────────────────
export function calculateOnboarding(
  inputs: SliderInput,
  currencyConfig: CurrencyConfig,
  productivity: ProductivityDetails,
): OnboardingDetails {
  const { desktop: dProd, frontline: fProd } = productivity;

  // Desktop
  const dNewHires: [number, number, number] = [
    Math.round(dProd.employees[0] - dProd.employees[0]), // yr1: totalDesktop - yr1 = 0
    Math.round(dProd.employees[1] - dProd.employees[0]),
    Math.round(dProd.employees[2] - dProd.employees[1]),
  ];

  const dChurn: [number, number, number] = dProd.employees.map((emp) =>
    Math.round((inputs.estimatedYearlyChurn / 100) * emp),
  ) as [number, number, number];

  const dOnboarding: [number, number, number] = [0, 0, 0].map((_, i) =>
    Math.round(dNewHires[i] + dChurn[i]),
  ) as [number, number, number];

  const dThreeMonthCost: [number, number, number] = [0, 0, 0].map((_, i) =>
    Math.round(dOnboarding[i] * dProd.hourlyRate[i] * WORKING_HOURS_PER_MONTH * INFLATION_INCREASE),
  ) as [number, number, number];

  const dLostProd: [number, number, number] = dThreeMonthCost.map((c) =>
    Math.round((PRODUCTIVITY_LEVEL_FIRST_THREE_MONTHS / 100) * c),
  ) as [number, number, number];

  const dSavedProd: [number, number, number] = [0, 0, 0].map((_, i) =>
    Math.round(
      dLostProd[i] -
        (dThreeMonthCost[i] *
          (PRODUCTIVITY_LEVEL_FIRST_THREE_MONTHS - DESKTOP_PRODUCTIVITY_IMPROVEMENT)) /
          100,
    ),
  ) as [number, number, number];

  const dFinalAdjusted: [number, number, number] = [0, 0, 0].map((_, i) => {
    const diff = dLostProd[i] - dSavedProd[i];
    return Math.round((DESKTOP_TIME_CAPTURED_PRODUCTIVITY / 100) * diff);
  }) as [number, number, number];

  const dSavingOnboarding: [number, number, number] = dSavedProd.map((v) =>
    Math.round((v * UNFORESEEABLE_BUSINESS_CHANGE) / 100),
  ) as [number, number, number];

  const dSavingTotal = Math.round(
    dSavingOnboarding[0] + dSavingOnboarding[1] + dSavingOnboarding[2],
  );

  // Frontline
  const fNewHires: [number, number, number] = [
    fProd.employees[0] - fProd.employees[0], // 0
    fProd.employees[1] - fProd.employees[0],
    fProd.employees[2] - fProd.employees[1],
  ];

  const fChurn: [number, number, number] = fProd.employees.map((emp) =>
    Math.round((inputs.estimatedYearlyChurn / 100) * emp),
  ) as [number, number, number];

  const fOnboarding: [number, number, number] = [0, 0, 0].map((_, i) =>
    Math.round(fNewHires[i] + fChurn[i]),
  ) as [number, number, number];

  const fThreeMonthCost: [number, number, number] = [0, 0, 0].map((_, i) =>
    Math.round(fOnboarding[i] * fProd.hourlyRate[i] * WORKING_HOURS_PER_MONTH * INFLATION_INCREASE),
  ) as [number, number, number];

  const fLostProd: [number, number, number] = fThreeMonthCost.map((c) =>
    Math.round((PRODUCTIVITY_LEVEL_FIRST_THREE_MONTHS / 100) * c),
  ) as [number, number, number];

  const fSavedProd: [number, number, number] = [0, 0, 0].map((_, i) =>
    Math.round(
      fLostProd[i] -
        (fThreeMonthCost[i] *
          (PRODUCTIVITY_LEVEL_FIRST_THREE_MONTHS - FRONTLINE_PRODUCTIVITY_IMPROVEMENT)) /
          100,
    ),
  ) as [number, number, number];

  const fFinalAdjusted: [number, number, number] = [0, 0, 0].map((_, i) => {
    const diff = fLostProd[i] - fSavedProd[i];
    return Math.round((FRONTLINE_TIME_CAPTURED_PRODUCTIVITY / 100) * diff);
  }) as [number, number, number];

  const fSavingOnboarding: [number, number, number] = fSavedProd.map((v) =>
    Math.round((UNFORESEEABLE_BUSINESS_CHANGE * v) / 100),
  ) as [number, number, number];

  const fSavingTotal = Math.round(
    fSavingOnboarding[0] + fSavingOnboarding[1] + fSavingOnboarding[2],
  );

  return {
    desktop: {
      newHires: dNewHires,
      expectedChurn: dChurn,
      totalOnboarding: dOnboarding,
      threeMonthCost: dThreeMonthCost,
      lostProductivityCost: dLostProd,
      savedProductivityValue: dSavedProd,
      finalAdjustedCost: dFinalAdjusted,
      savingInOnboarding: dSavingOnboarding,
      savingTotal: dSavingTotal,
    },
    frontline: {
      newHires: fNewHires,
      expectedChurn: fChurn,
      totalOnboarding: fOnboarding,
      threeMonthCost: fThreeMonthCost,
      lostProductivityCost: fLostProd,
      savedProductivityValue: fSavedProd,
      finalAdjustedCost: fFinalAdjusted,
      savingInOnboarding: fSavingOnboarding,
      savingTotal: fSavingTotal,
    },
  };
}

// ─── Retention Returns ──────────────────────────────────────────
export function calculateRetention(
  inputs: SliderInput,
  productivity: ProductivityDetails,
): RetentionDetails {
  const { desktop: dProd, frontline: fProd } = productivity;

  // Desktop
  const dSalary = inflateSalary(inputs.averageDesktopSalary);
  const dChurn: [number, number, number] = dProd.employees.map((emp) =>
    Math.round((inputs.estimatedYearlyChurn / 100) * emp),
  ) as [number, number, number];

  const dTotalCost: [number, number, number] = [0, 0, 0].map((_, i) =>
    Math.round(((dSalary[i] * DESKTOP_REPLACEMENT_COST_PERCENT) / 100) * dChurn[i]),
  ) as [number, number, number];

  const dReducedCost: [number, number, number] = [0, 0, 0].map((_, i) =>
    Math.round(
      ((dSalary[i] * 2) / 10) *
        (dProd.employees[i] *
          (inputs.estimatedYearlyChurn / 100 - DESKTOP_INTRANET_CHURN_IMPACT / 100)),
    ),
  ) as [number, number, number];

  const dApproxSaving: [number, number, number] = [0, 0, 0].map((_, i) =>
    Math.round(dTotalCost[i] - dReducedCost[i]),
  ) as [number, number, number];

  const dSavingsChurn: [number, number, number] = dApproxSaving.map((v) =>
    Math.round((v * UNFORESEEABLE_BUSINESS_CHANGE) / 100),
  ) as [number, number, number];

  const dSavingsTotal = Math.round(dSavingsChurn[0] + dSavingsChurn[1] + dSavingsChurn[2]);

  // Frontline
  const fSalary = inflateSalary(inputs.averageFrontlineSalary);
  const fChurn: [number, number, number] = fProd.employees.map((emp) =>
    Math.round((inputs.estimatedYearlyChurn / 100) * emp),
  ) as [number, number, number];

  const fTotalCost: [number, number, number] = [0, 0, 0].map((_, i) =>
    Math.round(((fSalary[i] * FRONTLINE_REPLACEMENT_COST_PERCENT) / 100) * fChurn[i]),
  ) as [number, number, number];

  const fReducedCost: [number, number, number] = [0, 0, 0].map((_, i) =>
    Math.round(
      ((fSalary[i] * 15) / 100) *
        (fProd.employees[i] *
          (inputs.estimatedYearlyChurn / 100 - FRONTLINE_INTRANET_CHURN_IMPACT / 100)),
    ),
  ) as [number, number, number];

  const fApproxSaving: [number, number, number] = [0, 0, 0].map((_, i) =>
    Math.round(fTotalCost[i] - fReducedCost[i]),
  ) as [number, number, number];

  const fSavingsChurn: [number, number, number] = fApproxSaving.map((v) =>
    Math.round((v * UNFORESEEABLE_BUSINESS_CHANGE) / 100),
  ) as [number, number, number];

  const fSavingsTotal = Math.round(fSavingsChurn[0] + fSavingsChurn[1] + fSavingsChurn[2]);

  return {
    desktop: {
      averageSalary: dSalary,
      churnPerYear: dChurn,
      totalReplacementCost: dTotalCost,
      reducedReplacementCost: dReducedCost,
      approximateSaving: dApproxSaving,
      savingsForChurn: dSavingsChurn,
      savingsTotal: dSavingsTotal,
    },
    frontline: {
      averageSalary: fSalary,
      churnPerYear: fChurn,
      totalReplacementCost: fTotalCost,
      reducedReplacementCost: fReducedCost,
      approximateSaving: fApproxSaving,
      savingsForChurn: fSavingsChurn,
      savingsTotal: fSavingsTotal,
    },
  };
}

// ─── Full ROI Calculation ───────────────────────────────────────
export function calculateRoi(inputs: SliderInput, currencyConfig: CurrencyConfig): RoiResults {
  const productivity = calculateProductivity(inputs, currencyConfig);
  const onboarding = calculateOnboarding(inputs, currencyConfig, productivity);
  const retention = calculateRetention(inputs, productivity);

  const prodTotal = (i: number) =>
    Math.round(productivity.desktop.adjustedValue[i] + productivity.frontline.adjustedValue[i]);
  const onbTotal = (i: number) =>
    Math.round(
      onboarding.desktop.savingInOnboarding[i] + onboarding.frontline.savingInOnboarding[i],
    );
  const retTotal = (i: number) =>
    Math.round(retention.desktop.savingsForChurn[i] + retention.frontline.savingsForChurn[i]);

  const productivityTotals = {
    year1: prodTotal(0),
    year2: prodTotal(1),
    year3: prodTotal(2),
    total: Math.round(productivity.desktop.adjustedTotal + productivity.frontline.adjustedTotal),
  };

  const onboardingTotals = {
    year1: onbTotal(0),
    year2: onbTotal(1),
    year3: onbTotal(2),
    total: Math.round(onboarding.desktop.savingTotal + onboarding.frontline.savingTotal),
  };

  const retentionTotals = {
    year1: retTotal(0),
    year2: retTotal(1),
    year3: retTotal(2),
    total: Math.round(retention.desktop.savingsTotal + retention.frontline.savingsTotal),
  };

  // Note: Bug preserved from old Vue app — year 2 uses retention year 3
  const totalRoi = {
    year1: Math.round(productivityTotals.year1 + onboardingTotals.year1 + retentionTotals.year1),
    year2: Math.round(productivityTotals.year2 + onboardingTotals.year2 + retentionTotals.year3),
    year3: Math.round(productivityTotals.year3 + onboardingTotals.year3 + retentionTotals.year3),
    total: Math.abs(
      Math.round(productivityTotals.total + onboardingTotals.total + retentionTotals.total),
    ),
  };

  return {
    productivity: {
      desktop: {
        year1: productivity.desktop.adjustedValue[0],
        year2: productivity.desktop.adjustedValue[1],
        year3: productivity.desktop.adjustedValue[2],
        total: productivity.desktop.adjustedTotal,
      },
      frontline: {
        year1: productivity.frontline.adjustedValue[0],
        year2: productivity.frontline.adjustedValue[1],
        year3: productivity.frontline.adjustedValue[2],
        total: productivity.frontline.adjustedTotal,
      },
      total: productivityTotals,
    },
    onboarding: {
      desktop: {
        year1: onboarding.desktop.savingInOnboarding[0],
        year2: onboarding.desktop.savingInOnboarding[1],
        year3: onboarding.desktop.savingInOnboarding[2],
        total: onboarding.desktop.savingTotal,
      },
      frontline: {
        year1: onboarding.frontline.savingInOnboarding[0],
        year2: onboarding.frontline.savingInOnboarding[1],
        year3: onboarding.frontline.savingInOnboarding[2],
        total: onboarding.frontline.savingTotal,
      },
      total: onboardingTotals,
    },
    retention: {
      desktop: {
        year1: retention.desktop.savingsForChurn[0],
        year2: retention.desktop.savingsForChurn[1],
        year3: retention.desktop.savingsForChurn[2],
        total: retention.desktop.savingsTotal,
      },
      frontline: {
        year1: retention.frontline.savingsForChurn[0],
        year2: retention.frontline.savingsForChurn[1],
        year3: retention.frontline.savingsForChurn[2],
        total: retention.frontline.savingsTotal,
      },
      total: retentionTotals,
    },
    totalRoi,
  };
}

// Re-export detail calculators for table rendering
export { calculateProductivity as getProductivityDetails };
export { calculateOnboarding as getOnboardingDetails };
export { calculateRetention as getRetentionDetails };
